import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import WallElementEditor from './WallElementEditor';
import { convertToInches, formatValue } from '@/utils/utils';
import { drawRoom, getWallCoordinates } from '@/utils/roomDrawingFunctions';
import { MousePointerClick } from 'lucide-react';
import RoomTypeSelector from './RoomTypeSelector';

const defaultFloorPlan = {
    roomType: 'u-shaped',
    unit: 'in',
    walls: {
        left: 110,
        back: 120,
        right: 110,
        front: 120,
        openFront: 30,
        lShortSide: 50,
        lLongSide: 140,
        lFrontR: 60,
        lFrontL: 60,
        lRight: 60,
    },
    roomHeight: 96,
};

const MIN_CANVAS_WIDTH = 320; // Minimum width in pixels, adjust as needed

const ManualFloorPlanEditor = ({ onSave, initialFloorPlan }) => {
    const [roomType, setRoomType] = useState(initialFloorPlan?.roomType || defaultFloorPlan.roomType);
    const [unit, setUnit] = useState(initialFloorPlan?.unit || defaultFloorPlan.unit);
    const [walls, setWalls] = useState(initialFloorPlan?.walls || defaultFloorPlan.walls);
    const [roomHeight, setRoomHeight] = useState(initialFloorPlan?.roomHeight || defaultFloorPlan.roomHeight);
    const [inputValues, setInputValues] = useState({});
    const [wallElements, setWallElements] = useState(initialFloorPlan?.wallElements || {});
    const [isWallDialogOpen, setIsWallDialogOpen] = useState(false);
    const [activeWall, setActiveWall] = useState(null);
    const [wallCoordinates, setWallCoordinates] = useState(null);
    const canvasRef = useRef(null);
    const { toast } = useToast();
    const [startX, setStartX] = useState(0);
    const [startY, setStartY] = useState(0);
    const [scale, setScale] = useState(1);
    const containerRef = useRef(null);
    const [canvasSize, setCanvasSize] = useState({ width: 400, height: 300 });

    useEffect(() => {
        const updateCanvasSize = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                const containerHeight = window.innerHeight * 0.6; // 60% of viewport height
                const aspectRatio = 4 / 3;

                // Calculate the size that maintains aspect ratio and fits container
                let newWidth = Math.max(MIN_CANVAS_WIDTH, Math.min(containerWidth, 400));
                let newHeight = newWidth / aspectRatio;

                if (newHeight > containerHeight) {
                    newHeight = containerHeight;
                    newWidth = newHeight * aspectRatio;
                }

                // Set the canvas's internal dimensions
                if (canvasRef.current) {
                    canvasRef.current.width = newWidth;
                    canvasRef.current.height = newHeight;
                }

                setCanvasSize({ width: newWidth, height: newHeight });
            }
        };

        updateCanvasSize();
        window.addEventListener('resize', updateCanvasSize);
        return () => window.removeEventListener('resize', updateCanvasSize);
    }, []);

    // Update the useEffect to handle the async drawRoomPlan
    useEffect(() => {
        const updateCanvas = async () => {
            try {
                await drawRoomPlan();
            } catch (error) {
                console.error('Error updating canvas:', error);
            }
        };

        updateCanvas();
    }, [walls, roomType, unit, wallElements, roomHeight, canvasSize]);

    useEffect(() => {
        const newInputValues = {
            roomHeight: formatValue(roomHeight, unit),
        };
        Object.entries(walls).forEach(([wall, value]) => {
            newInputValues[wall] = formatValue(value, unit);
        });
        setInputValues(newInputValues);
    }, [unit, walls, roomType, roomHeight]);

    const handleRoomTypeChange = newRoomType => {
        setRoomType(newRoomType);
    };

    const handleRoomHeightChange = value => {
        setInputValues(prev => ({ ...prev, roomHeight: value }));
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            setRoomHeight(convertToInches(numValue, unit));
        }
    };

    const handleWallChange = (wall, value) => {
        setInputValues(prev => ({ ...prev, [wall]: value }));
        const numValue = parseFloat(value);
        const MIN_WALL_LENGTH = 10; // Minimum wall length in inches

        if (convertToInches(numValue, unit) < MIN_WALL_LENGTH) {
            toast({
                title: 'Invalid wall length',
                description: `Wall length must be at least ${formatValue(MIN_WALL_LENGTH, unit)} ${unit}.`,
                variant: 'destructive',
            });
            return;
        }
        if (!isNaN(numValue)) {
            setWalls(prevWalls => {
                const newWalls = { ...prevWalls, [wall]: convertToInches(numValue, unit) };

                // Logic for rectangular room
                if (roomType === 'rectangular') {
                    if (wall === 'back' || wall === 'front') {
                        newWalls.back = newWalls.front = newWalls[wall];
                    } else if (wall === 'left' || wall === 'right') {
                        newWalls.left = newWalls.right = newWalls[wall];
                    }
                }

                // Logic for L-shaped room
                else if (roomType === 'l-shaped') {
                    if (wall === 'back') {
                        // Ensure lFrontR + lFrontL = back
                        const difference = newWalls.back - (newWalls.lFrontR + newWalls.lFrontL);
                        newWalls.lFrontR += difference / 2;
                        newWalls.lFrontL += difference / 2;
                    } else if (wall === 'lFrontR' || wall === 'lFrontL') {
                        // Adjust the other front wall to maintain total back wall length
                        const otherWall = wall === 'lFrontR' ? 'lFrontL' : 'lFrontR';
                        newWalls[otherWall] = newWalls.back - newWalls[wall];
                    } else if (wall === 'left') {
                        // Ensure left = lRight + lShortSide
                        const difference = newWalls.left - (newWalls.lRight + newWalls.lShortSide);
                        newWalls.lRight += difference / 2;
                        newWalls.lShortSide += difference / 2;
                    } else if (wall === 'lRight' || wall === 'lShortSide') {
                        // Adjust the other side wall to maintain total left wall length
                        const otherWall = wall === 'lRight' ? 'lShortSide' : 'lRight';
                        newWalls[otherWall] = newWalls.left - newWalls[wall];
                    }
                }

                return newWalls;
            });
        }
    };

    // Add these utility functions at the top level
    const calculateMaxWidth = coordinates => {
        if (!coordinates) return 0;
        let maxX = -Infinity;
        let minX = Infinity;

        Object.values(coordinates).forEach(wall => {
            maxX = Math.max(maxX, wall.x1, wall.x2);
            minX = Math.min(minX, wall.x1, wall.x2);
        });

        return maxX - minX;
    };

    const calculateMaxDepth = coordinates => {
        if (!coordinates) return 0;
        let maxY = -Infinity;
        let minY = Infinity;

        Object.values(coordinates).forEach(wall => {
            maxY = Math.max(maxY, wall.y1, wall.y2);
            minY = Math.min(minY, wall.y1, wall.y2);
        });

        return maxY - minY;
    };

    const drawRoomPlan = async () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Clear the canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Get wall coordinates first
        const wallCoordinates = getWallCoordinates(walls, roomType);
        setWallCoordinates(wallCoordinates);

        // Calculate required dimensions based on wall coordinates
        const requiredWidth = calculateMaxWidth(wallCoordinates);
        const requiredHeight = calculateMaxDepth(wallCoordinates);

        // Calculate scale to fit the room in the canvas with padding
        const padding = 40;
        const scaleX = (canvas.width - padding * 2) / requiredWidth;
        const scaleY = (canvas.height - padding * 2) / requiredHeight;
        const newScale = Math.min(scaleX, scaleY);
        setScale(newScale);

        // Calculate starting position to center the drawing
        const newStartX = (canvas.width - requiredWidth * newScale) / 2;
        const newStartY = (canvas.height - requiredHeight * newScale) / 2;
        setStartX(newStartX);
        setStartY(newStartY);

        // Draw grid
        ctx.strokeStyle = '#E5E5E5';
        ctx.lineWidth = 0.5;
        const gridSize = 12 * newScale; // 12 inches per grid cell

        // Draw vertical grid lines
        for (let x = newStartX % gridSize; x < canvas.width; x += gridSize) {
            ctx.beginPath();
            ctx.moveTo(x, 0);
            ctx.lineTo(x, canvas.height);
            ctx.stroke();
        }

        // Draw horizontal grid lines
        for (let y = newStartY % gridSize; y < canvas.height; y += gridSize) {
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(canvas.width, y);
            ctx.stroke();
        }

        // Reset line style for room drawing
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'black';

        try {
            // Draw the room asynchronously
            await drawRoom(ctx, walls, wallCoordinates, wallElements, newStartX, newStartY, newScale, unit, false);
        } catch (error) {
            console.error('Error drawing room:', error);
        }
    };

    const handleCanvasMouseMove = event => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();

        const x = (event.clientX - rect.left) * (canvas.width / rect.width);
        const y = (event.clientY - rect.top) * (canvas.height / rect.height);

        const isOverWall = findClickedWall(x, y);
        canvas.style.cursor = isOverWall ? 'pointer' : 'default';
    };

    const handleCanvasClick = event => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();

        const x = (event.clientX - rect.left) * (canvas.width / rect.width);
        const y = (event.clientY - rect.top) * (canvas.height / rect.height);

        const clickedWall = findClickedWall(x, y);
        if (clickedWall) {
            setActiveWall(clickedWall);
            setIsWallDialogOpen(true);
        }
    };

    const findClickedWall = (x, y) => {
        const wallCoordinates = getWallCoordinates(walls, roomType);
        setWallCoordinates(wallCoordinates);

        for (const [wall, coords] of Object.entries(wallCoordinates)) {
            if (
                isPointNearLine(
                    x,
                    y,
                    coords.x1 * scale + startX,
                    coords.y1 * scale + startY,
                    coords.x2 * scale + startX,
                    coords.y2 * scale + startY
                )
            ) {
                return wall;
            }
        }
        return null;
    };

    const isPointNearLine = (px, py, x1, y1, x2, y2, threshold = 10) => {
        const A = px - x1;
        const B = py - y1;
        const C = x2 - x1;
        const D = y2 - y1;

        const dot = A * C + B * D;
        const len_sq = C * C + D * D;
        const param = len_sq !== 0 ? dot / len_sq : -1;

        let xx, yy;

        if (param < 0) {
            xx = x1;
            yy = y1;
        } else if (param > 1) {
            xx = x2;
            yy = y2;
        } else {
            xx = x1 + param * C;
            yy = y1 + param * D;
        }

        const dx = px - xx;
        const dy = py - yy;

        return Math.sqrt(dx * dx + dy * dy) <= threshold;
    };

    const handleWallElementsChange = newElements => {
        setWallElements(prev => {
            const updated = { ...prev, [activeWall]: newElements };
            setTimeout(() => drawRoomPlan(), 0);
            return updated;
        });
    };

    useEffect(() => {
        const calculateMaxWidth = coordinates => {
            if (!coordinates) return 0;

            let maxX = 0;
            let minX = 0;
            try {
                Object.values(coordinates).forEach(wall => {
                    maxX = Math.max(maxX, wall.x1, wall.x2);
                    minX = Math.min(minX, wall.x1, wall.x2);
                });
            } catch (error) {
                console.error('Error calculating max width:', error);
            }
            return maxX - minX;
        };

        const calculateMaxDepth = coordinates => {
            if (!coordinates) return 0;

            let maxY = 0;
            let minY = 0;
            try {
                Object.values(coordinates).forEach(wall => {
                    maxY = Math.max(maxY, wall.y1, wall.y2);
                    minY = Math.min(minY, wall.y1, wall.y2);
                });
            } catch (error) {
                console.error('Error calculating max depth:', error);
            }
            return maxY - minY;
        };

        const newFloorPlanData = {
            roomType,
            unit,
            walls,
            dimensions: {
                width: calculateMaxWidth(wallCoordinates),
                depth: calculateMaxDepth(wallCoordinates),
                height: roomHeight,
            },
            wallElements,
            roomHeight,
            wallCoordinates,
        };

        onSave(newFloorPlanData);
    }, [roomType, unit, walls, wallElements, roomHeight, wallCoordinates]);

    const formatWallName = wall => {
        switch (wall) {
            case 'lShortSide':
                return 'Short wall';
            case 'lRight':
                return 'Right wall';
            case 'lFrontR':
                return 'Front (R) wall';
            case 'lFrontL':
                return 'Front (L) wall';
            case 'openFront':
                return 'Front wall';
            default:
                return wall;
        }
    };

    return (
        <div className="w-full max-w-3xl mx-auto" ref={containerRef}>
            <div className="p-4">
                <Card>
                    <CardHeader>
                        <p className="text-blue-800 font-semibold">
                            {' '}
                            Start by selecting your room shape, then proceed to customize your layout{' '}
                        </p>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-wrap items-center justify-center gap-4 mb-4">
                            <div className="flex items-center space-x-2">
                                <Label htmlFor="room-height">Room Height:</Label>
                                <Input
                                    id="room-height"
                                    type="number"
                                    value={inputValues.roomHeight || ''}
                                    onChange={e => handleRoomHeightChange(e.target.value)}
                                    onBlur={() => {
                                        const numValue = parseFloat(inputValues.roomHeight);
                                        if (isNaN(numValue)) {
                                            setInputValues(prev => ({
                                                ...prev,
                                                roomHeight: formatValue(roomHeight, unit),
                                            }));
                                        } else {
                                            setInputValues(prev => ({
                                                ...prev,
                                                roomHeight: formatValue(convertToInches(numValue, unit), unit),
                                            }));
                                        }
                                    }}
                                    className="w-24"
                                />
                            </div>

                            <div className="flex items-center  space-x-2">
                                <Label htmlFor="unit-select">Units:</Label>
                                <Select value={unit} onValueChange={setUnit}>
                                    <SelectTrigger id="unit-select" className="w-[120px]">
                                        <SelectValue placeholder="Select unit" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="in">Inches</SelectItem>
                                        <SelectItem value="ft">Feet</SelectItem>
                                        <SelectItem value="m">Meters</SelectItem>
                                        <SelectItem value="cm">Centimeters</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="w-full">
                                <RoomTypeSelector selectedType={roomType} onSelect={handleRoomTypeChange} />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
            {/* </ScrollArea> */}

            <Dialog open={isWallDialogOpen} onOpenChange={setIsWallDialogOpen}>
                <DialogContent className="max-w-[800px] w-full  max-h-[90vh]">
                    <DialogHeader>
                        <DialogTitle>Edit Wall: {formatWallName(activeWall)}</DialogTitle>
                    </DialogHeader>
                    <div className="flex items-center gap-2 mb-1">
                        <Label htmlFor="wall-width">Wall Width:</Label>
                        <Input
                            id="wall-width"
                            type="number"
                            value={inputValues[activeWall] || ''}
                            onChange={e => handleWallChange(activeWall, e.target.value)}
                            onBlur={() => {
                                const numValue = parseFloat(inputValues[activeWall]);
                                if (isNaN(numValue)) {
                                    setInputValues(prev => ({
                                        ...prev,
                                        [activeWall]: formatValue(walls[activeWall], unit),
                                    }));
                                } else {
                                    setInputValues(prev => ({
                                        ...prev,
                                        [activeWall]: formatValue(convertToInches(numValue, unit), unit),
                                    }));
                                }
                            }}
                            className="w-24"
                        />
                        <Label htmlFor="room-height">Room Height:</Label>
                        <Input
                            id="room-height"
                            type="number"
                            value={inputValues.roomHeight || ''}
                            onChange={e => handleRoomHeightChange(e.target.value)}
                            onBlur={() => {
                                const numValue = parseFloat(inputValues.roomHeight);
                                if (isNaN(numValue)) {
                                    setInputValues(prev => ({ ...prev, roomHeight: formatValue(roomHeight, unit) }));
                                } else {
                                    setInputValues(prev => ({
                                        ...prev,
                                        roomHeight: formatValue(convertToInches(numValue, unit), unit),
                                    }));
                                }
                            }}
                            className="w-24"
                        />
                        <span>{unit}</span>
                    </div>
                    {activeWall && (
                        <WallElementEditor
                            wallName={formatWallName(activeWall)}
                            wallWidth={walls[activeWall]}
                            wallHeight={roomHeight}
                            unit={unit}
                            elements={wallElements[activeWall] || []}
                            onElementsChange={handleWallElementsChange}
                            onClose={() => setIsWallDialogOpen(false)}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ManualFloorPlanEditor;
